import React, {useContext, useEffect, useState} from "react";
import {Button, FormControl, MenuItem, TextField, Tooltip} from "@material-ui/core";
import HelpIcon from "@material-ui/icons/Help";
import {Alert, Color} from "@material-ui/lab";
import {UIDialog} from "../../../../ui/feedback/UIDialog";
import {AsyncAutocompleteSelectComponent, DropDownValue} from "../components/AsyncAutocompleteSelect";
import {Compellable} from "../indexes/Indexes";
import {TransferTransporterByCountryTableStructure} from "../indexes/TransferTransporterByCountryIndexes";
import {RecordData, EditMode} from "../../presenters/pages/TransferTransporterByCountryPage";
import {
    upsertTransferTransporterByCountry,
    searchTransferTransporterByCountry
} from "../repository/TransferTransporterByCountryRepository";
import {useOktaAuth} from "../../../../okta-react";
import {AppContext, AppContextSpec} from "../../App";
import {DateObject} from "react-multi-date-picker";
import DatePicker from "react-multi-date-picker";
import Toolbar from "react-multi-date-picker/plugins/toolbar";
import gregorian from "react-date-object/calendars/gregorian";
import {createStyles, makeStyles, Theme} from "@material-ui/core/styles";

const useStyles = makeStyles((theme: Theme) => createStyles({
    fieldRow: {
        display: "flex",
        alignItems: "center",
        width: 550
    },
    tooltip: {marginLeft: theme.spacing(1)},
    defaultControl: {flexGrow: 1, margin: theme.spacing(1, 0)}
}));

export type EditFormModalOptions = {
    title: string;
    open: boolean;
    applyLabel: string;
    cancelLabel: string;
    onClose: () => void;
    onSuccess: () => Promise<void>;
};

type EditFormProps = {
    constraints: Compellable;
    record?: RecordData;
    modalOptions: EditFormModalOptions;
};

type TransferRecord = TransferTransporterByCountryTableStructure.RecordOf<TransferTransporterByCountryTableStructure.TransferTransporterByCountry>;

const isDefault = (value: unknown) => String(value).toUpperCase() === "Y";

export const EditForm = ({constraints, record, modalOptions}: EditFormProps) => {
    const styles = useStyles();
    const {authState} = useOktaAuth();
    const appContext = useContext<AppContextSpec>(AppContext);
    const [country, setCountry] = useState<DropDownValue | null>(null);
    const [transporter, setTransporter] = useState<DropDownValue | null>(null);
    const [defaultValue, setDefaultValue] = useState("");
    const [daysOff, setDaysOff] = useState<DateObject[]>([]);
    const [notification, setNotification] = useState<{severity: Color; message: string}>();
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const row = record?.record;
        setCountry(row ? {key: String(row.countryCode), value: String(row.countryCode)} : null);
        setTransporter(row ? {key: String(row.transporterCode), value: String(row.transporterCode)} : null);
        setDefaultValue(row ? String(row.defaultTransporter || "").toUpperCase() : "");
        const values = row?.daysOff ? String(row.daysOff).split(/[;,]/).filter(Boolean) : [];
        setDaysOff(values.map(value => {
            const [day, month] = value.trim().split("/").map(Number);
            return new DateObject().setCalendar(gregorian).setFormat("DD/MM")
                .setYear(new Date().getFullYear()).setMonth(month).setDay(day);
        }));
        setNotification(undefined);
    }, [record, modalOptions.open]);

    const currentCountryRows = async (countryCode: string): Promise<TransferRecord[]> => {
        const allRows: TransferRecord[] = [];
        let page = 1;
        let total = 0;
        do {
            const result = await searchTransferTransporterByCountry<TransferTransporterByCountryTableStructure.TransferTransporterByCountry>(
                authState!,
                [{key: "countryCode", value: countryCode}],
                page,
                100,
                "asc",
                "countryCode"
            );
            allRows.push(...result.values);
            total = result.pagination.total;
            page += 1;
        } while (allRows.length < total);
        return allRows;
    };

    const persist = async (form: Record<string, unknown>, rowsToDemote: TransferRecord[]) => {
        setSaving(true);
        try {
            for (const row of rowsToDemote) {
                await upsertTransferTransporterByCountry(authState!, {
                    toUpdate: row,
                    updateBy: {...row, defaultTransporter: "N"}
                });
            }
            await upsertTransferTransporterByCountry(authState!, {
                toUpdate: record?.type === EditMode.Edit ? record.record : undefined,
                updateBy: form
            });
            await modalOptions.onSuccess();
        } catch (error: any) {
            setNotification({severity: "error", message: error.message || "Failed to save transfer transporter"});
        } finally {
            setSaving(false);
        }
    };

    const submit = async () => {
        setNotification(undefined);
        if (!country || !transporter) {
            setNotification({severity: "error", message: "Country and transporter are required"});
            return;
        }
        if (constraints.daysOff.required && daysOff.length === 0) {
            setNotification({severity: "error", message: "Days Off are required"});
            return;
        }

        try {
            const rows = await currentCountryRows(country.key);
            if (
                record?.type === EditMode.Edit &&
                isDefault(record.record.defaultTransporter) &&
                String(record.record.countryCode) !== country.key
            ) {
                const previousCountryRows = await currentCountryRows(String(record.record.countryCode));
                const previousCountryHasAnotherDefault = previousCountryRows.some(row =>
                    String(row.id) !== String(record.record.id) && isDefault(row.defaultTransporter)
                );
                if (!previousCountryHasAnotherDefault) {
                    setNotification({
                        severity: "error",
                        message: `You should set another default transporter for ${record.record.countryCode} before changing this one.`
                    });
                    return;
                }
            }
            const otherRows = rows.filter(row => record?.type === EditMode.Edit
                ? String(row.id) !== String(record.record.id)
                : true);
            const otherDefaults = otherRows.filter(row => isDefault(row.defaultTransporter));
            let nextDefault = defaultValue;

            if (record?.type === EditMode.Edit && !nextDefault) {
                setNotification({severity: "error", message: "You should set Default field"});
                return;
            }
            if (!nextDefault) {
                if (rows.length === 0) nextDefault = "Y";
                else {
                    setNotification({severity: "error", message: "You should set Default field"});
                    return;
                }
            }
            if (nextDefault !== "Y" && nextDefault !== "N") {
                setNotification({severity: "error", message: "You should set Default field"});
                return;
            }

            const hasDefaultAfterSave = nextDefault === "Y" || otherDefaults.length > 0;
            if (!hasDefaultAfterSave) {
                setNotification({
                    severity: "error",
                    message: "No transporter is set as the default transporter for this country, you should set this one to Y"
                });
                return;
            }

            const form = {
                countryCode: country.key,
                defaultTransporter: nextDefault,
                transporterCode: transporter.key,
                daysOff: daysOff.map(day => day.format("DD/MM")).join(";")
            };
            const rowsToDemote = nextDefault === "Y" ? otherDefaults : [];
            if (rowsToDemote.length > 0) {
                appContext.prompt(
                    "Do you really want to set this transporter as the default transporter for this country, this will put others transporter for this country to N?",
                    confirmed => {
                        if (confirmed) void persist(form, rowsToDemote);
                    },
                    {confirmLabel: "Yes", rejectLabel: "No"}
                );
                return;
            }
            await persist(form, rowsToDemote);
        } catch (error: any) {
            setNotification({severity: "error", message: error.message || "Failed to validate the default transporter"});
        }
    };

    const getDefault = (field: string): DropDownValue | null => {
        if (!record) return null;
        const value = record.record[field];
        return value === undefined || value === null
            ? null
            : {key: String(value), value: String(value)};
    };

    const onDropdownChange = (id: string | number, value: DropDownValue | null) => {
        if (id === "edit-countryCode") setCountry(value);
        if (id === "edit-transporterCode") setTransporter(value);
    };

    const fieldOrder = ["countryCode", "transporterCode", "daysOff", "defaultTransporter"];

    return (
        <UIDialog open={modalOptions.open} onClose={modalOptions.onClose} title={modalOptions.title} actions={[
            <Button key="cancel" onClick={modalOptions.onClose}>{modalOptions.cancelLabel}</Button>,
            <Button key="submit" onClick={() => void submit()} variant="contained" color="primary" disabled={saving}>
                {modalOptions.applyLabel}
            </Button>
        ]}>
            {notification && <Alert severity={notification.severity}>{notification.message}</Alert>}
            <form autoComplete="off">
                {fieldOrder.map(field => {
                    const constraint = constraints[field];
                    if (!constraint) return null;
                    if (field === "defaultTransporter") {
                        return (
                            <div key={field} className={styles.fieldRow}>
                                <FormControl variant="outlined" size="small" className={styles.defaultControl} required>
                                    <TextField
                                        select
                                        label={constraint.displayName || "Default"}
                                        value={defaultValue}
                                        onChange={event => setDefaultValue(event.target.value)}
                                        variant="outlined"
                                        required
                                    >
                                        <MenuItem value="Y">Y</MenuItem>
                                        <MenuItem value="N">N</MenuItem>
                                    </TextField>
                                </FormControl>
                                {constraint.information && <Tooltip title={constraint.information} className={styles.tooltip}><HelpIcon /></Tooltip>}
                            </div>
                        );
                    }
                    if (field === "daysOff") {
                        return (
                            <div key={field} className={styles.fieldRow}>
                                <DatePicker
                                    placeholder={constraint.displayName || "Days Off"}
                                    sort
                                    className="bg-dark"
                                    value={daysOff}
                                    onChange={(values: DateObject[]) => setDaysOff(values || [])}
                                    containerStyle={{width: "100%"}}
                                    multiple
                                    disableYearPicker
                                    hideYear
                                    format="DD/MM"
                                    style={{width: "100%", fontSize: "medium", padding: "6px 12px", boxSizing: "border-box", height: 40, marginTop: 20}}
                                    plugins={[<Toolbar key="toolbar" position="bottom" />]}
                                />
                                {constraint.information && <Tooltip title={constraint.information} className={styles.tooltip}><HelpIcon /></Tooltip>}
                            </div>
                        );
                    }
                    return (
                        <div key={field} className={styles.fieldRow}>
                            <AsyncAutocompleteSelectComponent
                                id={`edit-${field}`}
                                key={`${field}-${record?.record.id || "new"}`}
                                autocompleteContainerStyle={{flexGrow: 1}}
                                reference={field === "transporterCode" ? "transporterDaysOffWeek" : constraint.reference}
                                required={constraint.required}
                                information={constraint.information}
                                label={constraint.displayName}
                                defaultValue={getDefault(field)}
                                onParentChangeValue={onDropdownChange}
                            />
                            {constraint.information && <Tooltip title={constraint.information} className={styles.tooltip}><HelpIcon /></Tooltip>}
                        </div>
                    );
                })}
            </form>
        </UIDialog>
    );
};
